import * as React from 'react';
import axios from 'axios';
import { apiUrl } from 'utils/apiUrl';
import { useAuthContext } from 'contexts/AuthContext';
import { TenantState } from 'components/TenantLockStatus';

// The site list does not carry lock state, so it is read per tenant, the way the site page's
// lock switch reads it. Requests are capped and cached briefly so scrolling stays light.
const MAX_CONCURRENT_REQUESTS = 6;
const CACHE_TTL_MS = 60 * 1000;

/** Locked or not; null when the state could not be read */
export type LockState = boolean | null;

interface CacheEntry {
  promise: Promise<LockState>;
  at: number;
}

const cache = new Map<string, CacheEntry>();
const queue: (() => void)[] = [];
let active = 0;

const runQueued = <T>(task: () => Promise<T>): Promise<T> =>
  new Promise<T>((resolve, reject) => {
    const run = () => {
      active += 1;
      task()
        .then(resolve, reject)
        .finally(() => {
          active -= 1;
          queue.shift()?.();
        });
    };
    if (active < MAX_CONCURRENT_REQUESTS) {
      run();
    } else {
      queue.push(run);
    }
  });

const fetchLocked = (tenantId: string, idToken?: string | null): Promise<LockState> => {
  const cached = cache.get(tenantId);
  if (cached && Date.now() - cached.at < CACHE_TTL_MS) {
    return cached.promise;
  }
  const promise = runQueued(() =>
    axios.get<TenantState>(apiUrl('tenancy', `tenants/${tenantId}/state`), {
      headers: { Authorization: `Bearer ${idToken}` },
    }),
  )
    .then(({ data }) => data?.Locked === true)
    // A tenant that never had a state saved is not locked; any other failure is unknown, and is
    // shown as such rather than as unlocked
    .catch((error) => (error?.response?.status === 404 ? false : null));
  cache.set(tenantId, { promise, at: Date.now() });
  return promise;
};

/** For tests: forget cached lock states. */
export const clearSiteLockCache = () => cache.clear();

/** The current id token, read when a request is sent so a token refresh does not start over. */
const useIdTokenRef = () => {
  const { idToken } = useAuthContext();
  const idTokenRef = React.useRef(idToken);
  idTokenRef.current = idToken;
  return idTokenRef;
};

/**
 * Whether a site's tenant is locked, read only once `enabled` (the Locked filter is on and the row
 * is on screen). Undefined until read, null when it could not be read.
 */
export const useSiteLocked = (tenantId: string | undefined, enabled: boolean) => {
  const idTokenRef = useIdTokenRef();
  const [locked, setLocked] = React.useState<LockState | undefined>(undefined);

  React.useEffect(() => {
    if (!enabled || !tenantId) return undefined;
    let cancelled = false;
    fetchLocked(tenantId, idTokenRef.current).then((value) => {
      if (!cancelled) {
        setLocked(value);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [tenantId, enabled, idTokenRef]);

  return locked;
};

/**
 * True once the element has been on screen. Without IntersectionObserver (old browsers, jsdom)
 * every element counts as on screen.
 */
export const useHasBeenOnScreen = <T extends Element>(ref: React.RefObject<T>) => {
  const [seen, setSeen] = React.useState(false);

  React.useEffect(() => {
    if (seen) return undefined;
    const element = ref.current;
    if (!element || typeof IntersectionObserver === 'undefined') {
      setSeen(true);
      return undefined;
    }
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        setSeen(true);
        observer.disconnect();
      }
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, [ref, seen]);

  return seen;
};

export type LockStates = Record<string, LockState | undefined>;

export interface UseLockStatesResult {
  /** Lock state by tenant id: undefined until read, null when it could not be read */
  locks: LockStates;
}

/**
 * Lock state of every given tenant, for filtering and sorting the whole loaded list. Uses the
 * same cache and request cap as the per-row lookup, and only runs while `enabled`: the Locked
 * filter narrows the list or the list is sorted by lock state.
 */
export const useLockStates = (tenantIds: string[], enabled: boolean): UseLockStatesResult => {
  const idTokenRef = useIdTokenRef();
  const [locks, setLocks] = React.useState<LockStates>({});
  const requestedRef = React.useRef(new Set<string>());
  const answeredRef = React.useRef(new Set<string>());

  React.useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    // The same Set for the hook's lifetime, filled in place
    const answered = answeredRef.current;
    // Answers arrive one by one; apply them in batches so the list does not re-sort per site
    let buffered: LockStates = {};
    let flushTimer: ReturnType<typeof setTimeout> | undefined;
    const flush = () => {
      flushTimer = undefined;
      const batch = buffered;
      buffered = {};
      Object.keys(batch).forEach((tenantId) => answered.add(tenantId));
      if (!cancelled) {
        setLocks((prev) => ({ ...prev, ...batch }));
      }
    };

    tenantIds
      .filter((tenantId) => tenantId && !requestedRef.current.has(tenantId))
      .forEach((tenantId) => {
        requestedRef.current.add(tenantId);
        fetchLocked(tenantId, idTokenRef.current).then((value) => {
          buffered[tenantId] = value;
          if (!flushTimer) {
            flushTimer = setTimeout(flush, 200);
          }
        });
      });

    return () => {
      cancelled = true;
      if (flushTimer) {
        clearTimeout(flushTimer);
      }
      // Anything still in flight is asked again (from the cache) on the next run
      requestedRef.current = new Set(answered);
    };
  }, [tenantIds, enabled, idTokenRef]);

  return { locks };
};
